import { MapPin, Ticket, ArrowRight, Calendar, ChevronDown, ChevronUp, Globe, Navigation, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { usePersonalizedFeed, FeedConcert } from "@/hooks/usePersonalizedFeed";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useState, useCallback } from "react";
import { toast } from "@/hooks/use-toast";

interface ArtistGroup {
  artist: string;
  artistId: string;
  artistImage: string;
  events: FeedConcert[];
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return { month: "", day: "", weekday: "" };
  const d = new Date(dateStr + "T00:00:00");
  return {
    month: d.toLocaleDateString("en-US", { month: "short" }),
    day: String(d.getDate()),
    weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
  };
};

function groupByArtist(concerts: FeedConcert[]): ArtistGroup[] {
  const map = new Map<string, ArtistGroup>();
  for (const c of concerts) {
    const key = c.artist.toLowerCase();
    if (!map.has(key)) {
      map.set(key, {
        artist: c.artist,
        artistId: c.artistId,
        artistImage: c.artistImage,
        events: [],
      });
    }
    map.get(key)!.events.push(c);
  }
  return [...map.values()];
}

const ArtistGroupCard = ({ group }: { group: ArtistGroup }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const hasMultiple = group.events.length > 1;
  const visibleEvents = expanded ? group.events : group.events.slice(0, 1);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="rounded-2xl border border-border/50 bg-card overflow-hidden hover:border-primary/20 hover:shadow-elevated transition-all"
    >
      <div
        className="flex items-center gap-4 p-4 sm:p-5 cursor-pointer group"
        onClick={() => group.artistId ? navigate(`/artist/${group.artistId}`) : null}
      >
        {group.artistImage ? (
          <img
            src={group.artistImage}
            alt={group.artist}
            loading="lazy"
            width={56}
            height={56}
            className="h-14 w-14 flex-shrink-0 rounded-full object-cover border-2 border-border/50 group-hover:border-primary/40 transition-colors"
          />
        ) : (
          <div className="h-14 w-14 flex-shrink-0 rounded-full bg-secondary flex items-center justify-center">
            <Calendar className="h-6 w-6 text-muted-foreground" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-foreground group-hover:text-primary transition-colors truncate">
            {group.artist}
          </h3>
          <p className="text-sm text-muted-foreground">
            {group.events.length} date{group.events.length !== 1 ? "s" : ""}
          </p>
        </div>
        {hasMultiple && (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="flex items-center gap-1 text-xs font-display font-semibold text-primary hover:text-primary/80 transition-colors px-3 py-1.5 rounded-full bg-primary/10"
          >
            {expanded ? (
              <><ChevronUp className="h-3.5 w-3.5" /> Less</>
            ) : (
              <><ChevronDown className="h-3.5 w-3.5" /> {group.events.length - 1} more</>
            )}
          </button>
        )}
      </div>

      <AnimatePresence initial={false}>
        <div className="border-t border-border/30">
          {visibleEvents.map((concert, i) => {
            const { month, day, weekday } = formatDate(concert.date);
            return (
              <motion.div
                key={concert.id}
                initial={i > 0 ? { height: 0, opacity: 0 } : false}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div
                  className="flex items-center justify-between px-4 sm:px-5 py-3.5 hover:bg-secondary/30 cursor-pointer transition-colors group/event"
                  onClick={() => navigate(`/event/${concert.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 flex-shrink-0 flex-col items-center justify-center rounded-xl gradient-hero text-primary-foreground font-display font-bold text-xs leading-tight shadow-md shadow-primary/20">
                      <span className="text-[9px] uppercase font-semibold opacity-80">{month}</span>
                      <span className="text-base -mt-0.5">{day}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-display font-semibold text-foreground group-hover/event:text-primary transition-colors">
                          {weekday}
                        </span>
                        {concert.price && (
                          <span className="text-xs text-muted-foreground font-display">{concert.price}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        {concert.venue && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> {concert.venue}
                          </span>
                        )}
                        {concert.city && (
                          <span className="hidden sm:inline">{concert.city}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="gradient-hero border-0 font-display font-semibold rounded-full shadow-md shadow-primary/20 opacity-80 group-hover/event:opacity-100 transition-opacity text-xs h-8 px-3"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (concert.url) window.open(concert.url, "_blank");
                    }}
                  >
                    <Ticket className="mr-1 h-3 w-3" />
                    Tickets
                  </Button>
                </div>
                {i < visibleEvents.length - 1 && <div className="mx-5 border-t border-border/20" />}
              </motion.div>
            );
          })}
        </div>
      </AnimatePresence>
    </motion.div>
  );
};

type ViewMode = "global" | "nearby";

const UpcomingConcerts = () => {
  const { concerts, personalized, loading } = usePersonalizedFeed();
  const [viewMode, setViewMode] = useState<ViewMode>("global");
  const [nearbyConcerts, setNearbyConcerts] = useState<FeedConcert[]>([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [locationGranted, setLocationGranted] = useState(false);

  const fetchNearby = useCallback(async () => {
    setNearbyLoading(true);
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
        });
      });

      setLocationGranted(true);
      const { latitude, longitude } = position.coords;

      const { data, error } = await supabase.functions.invoke("personalized-feed", {
        body: { lat: latitude, lng: longitude, radius: 300 },
      });

      if (error) throw error;
      setNearbyConcerts(data?.concerts || []);
    } catch (e: any) {
      if (e?.code === 1) {
        toast({
          title: "Location access denied",
          description: "Please allow location access to see shows near you.",
          variant: "destructive",
        });
        setViewMode("global");
      } else {
        console.error("Nearby fetch error:", e);
        toast({
          title: "Couldn't find nearby shows",
          description: "Try again or browse global shows.",
          variant: "destructive",
        });
        setViewMode("global");
      }
    } finally {
      setNearbyLoading(false);
    }
  }, []);

  const handleToggle = (mode: ViewMode) => {
    setViewMode(mode);
    if (mode === "nearby" && !locationGranted) {
      fetchNearby();
    }
  };

  const isNearby = viewMode === "nearby";
  const activeConcerts = isNearby ? nearbyConcerts : concerts;
  const activeLoading = isNearby ? nearbyLoading : loading;
  const groups = groupByArtist(activeConcerts);

  return (
    <section className="py-14 sm:py-20 border-t border-border/50 relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-[500px] h-[300px] bg-concert-cool/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4"
        >
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-1 w-8 rounded-full bg-concert-cool" />
              <span className="text-xs font-display font-bold text-concert-cool uppercase tracking-widest">Events</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
              {isNearby ? "Shows Near You" : personalized ? "Shows For You" : "Popular Shows"}
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {isNearby
                ? "Live events within 100km of your location"
                : personalized
                  ? "Based on your activity & concert history"
                  : "Trending shows on Encore Republic"}
            </p>
          </div>

          {/* View toggle */}
          <div className="flex items-center gap-1 p-1 rounded-full bg-secondary/50 border border-border/50 self-start sm:self-auto">
            {([
              { mode: "global" as ViewMode, icon: Globe, label: "Global" },
              { mode: "nearby" as ViewMode, icon: Navigation, label: "Near You" },
            ]).map(({ mode, icon: Icon, label }) => (
              <Tooltip key={mode}>
                <TooltipTrigger asChild>
                  <motion.button
                    onClick={() => handleToggle(mode)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-display font-semibold transition-all ${
                      viewMode === mode
                        ? "bg-card text-foreground shadow-md"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    whileTap={{ scale: 0.97 }}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent>
                  {mode === "nearby" ? "Shows within 100km of you" : "Popular shows worldwide"}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </motion.div>

        <div className="space-y-3">
          {activeLoading ? (
            <>
              {isNearby && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center justify-center gap-3 py-8 text-muted-foreground"
                >
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  <span className="font-display text-sm">Finding shows near you…</span>
                </motion.div>
              )}
              {!isNearby && Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-border/50 bg-card p-4 sm:p-5">
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-14 w-14 rounded-full" />
                    <div>
                      <Skeleton className="h-5 w-32 mb-2" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : groups.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16 border border-border/50 rounded-2xl bg-card/30"
            >
              {isNearby ? (
                <>
                  <Navigation className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="font-display text-lg font-semibold text-foreground">No shows found nearby</p>
                  <p className="text-sm text-muted-foreground mt-1">Try browsing global shows instead</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4 rounded-full font-display"
                    onClick={() => setViewMode("global")}
                  >
                    <Globe className="mr-2 h-4 w-4" /> View Global Shows
                  </Button>
                </>
              ) : (
                <>
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="font-display text-lg font-semibold text-foreground">No upcoming shows</p>
                  <p className="text-sm text-muted-foreground mt-1">Check back later for new events</p>
                </>
              )}
            </motion.div>
          ) : (
            groups.map((group) => (
              <ArtistGroupCard key={group.artist} group={group} />
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default UpcomingConcerts;
